import React, { useEffect, useState } from 'react';
import {
    validateCardData,
    validateCheckData,
    validatePosData,
    validateContactInfoAndZeroCheck
} from "./paymentFunctions";
import {
    useCurrentUser,
    useRequestUrl,
    useAuthToken,
    useAgencyCourt,
    useCourtsByAgency,
    useTerminals
} from "../context/contextHooks";
import { useFinalizeLogEntry } from "../customHooks/useFinalizeLogEntry";
import API from '../http/API';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Box from "@mui/material/Box";
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Button from "@mui/material/Button";
import ReplayIcon from "@mui/icons-material/Replay";
import SendTwoToneIcon from '@mui/icons-material/SendTwoTone';
import CardData from './CardData';
import CheckData from './CheckData';
import PosData from "./PosData";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import {getDecimal} from "../config/utils";
import {useSxStyles as sx} from "../styles/sxStyles";

const CompletePayment = (props) => {
    const openDialog = props.openDialog;
    const setOpenDialog = props.setOpenDialog;
    const paymentRequest = props.paymentRequest;
    const setPaymentResult = props.setPaymentResult
    const court = props.court;
    const [requestUrl,] = useRequestUrl();
    const [authToken,] = useAuthToken();
    const [user,] = useCurrentUser();
    const finalizeLogEntry = useFinalizeLogEntry();
    const [tabVal, setTabVal] = useState('1');
    const [loading, setLoading] = useState(false);
    const [actionResponse, setActionResponse] = useState(null);
    const [paymentFields, setPaymentFields] = useState({paymentType: 'CARD', email: paymentRequest.email, phone: paymentRequest.phone});
    const [errorMessage, setErrorMessage] = useState(null);
    const [terminals, setTerminals] = useTerminals();
    const [courtsByAgency,] = useCourtsByAgency();
    const [agencyCourt, setAgencyCourt] = useAgencyCourt();
    const [pymtLocation, setPymtLocation] = useState(user ? user.location : null)
    const [noSubmit, setNoSubmit] = useState(false);

    const getTerminals = () => {
        setTerminals(null);
        setLoading(true);
        const url = `${requestUrl}/pos/getTerminals/${court.vantivAccepterId}`;
        API.getRetrieve(url, 'getTerminals', `Bearer ${authToken}`, setActionResponse);
    }

    const sendPaymentRequest = (request) => {
        setLoading(true);
        const url = `${requestUrl}/authorizePayment`;
        const payload = JSON.stringify(request);
        API.postRetrieve(url, payload, 'authorizePayment', `Bearer ${authToken}`, setActionResponse);
    }

    const handleTabChange = (e, newValue) => {
        setPaymentFields({...paymentFields, paymentType: (newValue === '2' ? 'CHECK' : newValue === '3' ? 'POS' : 'CARD')});
        setTabVal(newValue);
    }

    const handleSubmit = () => {
        setErrorMessage(null);
        let errors = paymentFields.paymentType === 'CHECK' ? validateCheckData(paymentFields, court) :
            (paymentFields.paymentType === 'POS' ? validatePosData(paymentFields) : validateCardData(paymentFields, court));
        if (errors) {
            setErrorMessage(errors);
        } else {
            paymentRequest.totalAmount = parseFloat(getDecimal(paymentRequest.totalAmount, false));
            paymentRequest.paymentOperator = user.externalId;
            const request = {...paymentRequest, ...paymentFields, paymentLocation: pymtLocation ? pymtLocation : user.location};
            errors = validateContactInfoAndZeroCheck(request, court);
            if (errors) {
                setErrorMessage(errors);
            } else {
                setNoSubmit(true);
                sendPaymentRequest(request);
            }
        }
    }

    useEffect(() => {
        if (loading && actionResponse) {
            if (actionResponse.error) {
                if (actionResponse.declineMessage) finalizeLogEntry(actionResponse, court, setActionResponse);
                setErrorMessage(actionResponse.errorMessage);
            } else if (actionResponse.requestType === 'getTerminals') {
                setTerminals(actionResponse.terminals);
            } else {
                finalizeLogEntry(actionResponse, court, setActionResponse);
                setPaymentResult(actionResponse);
                setOpenDialog(false);
            }
            setLoading(false);
            setNoSubmit(false);
        }
    }, [actionResponse])

    useEffect(() => {
        if (agencyCourt && user.posPayments && !loading && !terminals) {
            getTerminals();
        }
    }, [agencyCourt, user, loading, terminals])

    useEffect(() => {
        if (openDialog) setErrorMessage(null);
    }, [openDialog]);

    useEffect(() => {
        if (!agencyCourt && courtsByAgency && courtsByAgency.courts && courtsByAgency.courts.length === 1) {
            setAgencyCourt(courtsByAgency.courts[0]);
        }
    }, [courtsByAgency, agencyCourt]);

    return (
        <>
            <Dialog
                open={openDialog}
                maxWidth='sm'
                fullWidth
                sx={sx.dialog}
                slotProps={{paper: {sx: sx.listContainer}}}
            >
                <DialogTitle color='primary'>Enter Payment Information</DialogTitle>
                <Backdrop sx={sx.backdrop} open={loading}>
                    <CircularProgress color="inherit"/>
                </Backdrop>
                { errorMessage &&
                    <Alert severity='error' sx={{marginTop: '1rem', textAlign: 'left'}}>
                        <AlertTitle>Error!</AlertTitle>
                        {errorMessage}
                    </Alert>
                }
                <DialogContent dividers sx={sx.listContainer} >
                    <Box sx={{ width: '100%', typography: 'body1' }}>
                        <TabContext value={tabVal}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <TabList onChange={handleTabChange}>
                                    <Tab label='Card Payment' value='1'/>
                                    { !court.noChex &&
                                        <Tab label='Check Payment' value='2'/>
                                    }
                                    { user.posPayments && terminals && terminals.length > 0 &&
                                        <Tab label='POS Payment' value='3'/>
                                    }
                                </TabList>
                            </Box>
                            <TabPanel value='1'>
                                <CardData
                                    showTitle={false}
                                    paymentRequest={paymentRequest}
                                    paymentFields={paymentFields}
                                    setPaymentFields={setPaymentFields}
                                    locations={agencyCourt && agencyCourt.paymentSourceSystems ? agencyCourt.paymentSourceSystems : null}
                                    user={user}
                                    setLocation={setPymtLocation}
                                    court={court}
                                />
                            </TabPanel>
                            { !court.noChex &&
                                <TabPanel value='2'>
                                    <CheckData
                                        paymentRequest={paymentRequest}
                                        paymentFields={paymentFields}
                                        setPaymentFields={setPaymentFields}
                                        locations={agencyCourt && agencyCourt.paymentSourceSystems ? agencyCourt.paymentSourceSystems : null}
                                        user={user}
                                        setLocation={setPymtLocation}
                                    />
                                </TabPanel>
                            }
                            { user.posPayments && terminals && terminals.length > 0 &&
                                <TabPanel value='3'>
                                    <PosData
                                        terminals={terminals}
                                        paymentRequest={paymentRequest}
                                        paymentFields={paymentFields}
                                        setPaymentFields={setPaymentFields}
                                        court={court}
                                        locations={agencyCourt && agencyCourt.paymentSourceSystems ? agencyCourt.paymentSourceSystems : null}
                                        user={user}
                                        setLocation={setPymtLocation}
                                    />
                                </TabPanel>
                            }
                        </TabContext>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Box component='div' sx={{display: 'flex', flexDirection: 'row-reverse', margin: '1rem'}}>
                        <Button
                            variant='contained'
                            color='primary'
                            startIcon={<SendTwoToneIcon />}
                            disabled={noSubmit}
                            sx={sx.squashedButton}
                            onClick={() => handleSubmit()}
                        >
                            Submit
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<ReplayIcon/>}
                            sx={sx.squashedButton}
                            onClick={() => {
                                setOpenDialog(false);
                            }}
                            disabled={noSubmit}
                        >
                            Cancel
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default CompletePayment;
